import { useCallback, useState } from "react";

import { interpolateString } from "@helpers/string-utils";
import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";
import CitationSourceDialog from "@memorang/ui/components/SessionConfig/CitationSourceDialog";
import { PostAnswerPanel } from "@memorang/ui/components/StudySession/PostAnswerPanel";
import { useTranslation } from "react-i18next";
import { FeedbackDialogContainer } from "../../../containers/FeedbackDialogContainer";

import { useAppStore } from "@hooks/useAppStore";
import useTutorialCache from "@hooks/useTutorialCache";
import AiSessionFab from "../components/AiSessionFab";
import { usePostAnswerAppletValue } from "../hooks/usePostAnswerAppletValue";

// Module-level constant: compound components close over context, not props, so this is safe to hoist.
const postAnswerPanelChildren = (
	<PostAnswerPanel.Surface>
		<PostAnswerPanel.Body />
		<PostAnswerPanel.SuggestCorrection />
	</PostAnswerPanel.Surface>
);

const useDialogState = () => {
	const [visible, setVisible] = useState(false);

	const open = useCallback(() => {
		setVisible(true);
	}, []);

	const close = useCallback(() => {
		setVisible(false);
	}, []);

	const toggle = useCallback(() => {
		setVisible((currentVisible) => !currentVisible);
	}, []);

	return { close, open, toggle, visible };
};

const StudySessionFeedbackContainer = ({
	isSplitLayout,
}: {
	isSplitLayout?: boolean;
}) => {
	const { t } = useTranslation();
	const suggestCorrectionDialog = useDialogState();
	const aiDisclaimerDialog = useDialogState();
	const { setTutorialData } = useTutorialCache();
	const ailaEnabled = Boolean(
		useAppStore((store) => store.sessionView?.ai?.aila),
	);

	const {
		aiAssistantTitle,
		aiDisclaimer,
		citationsPageNumber,
		currentItem,
		handleCloseCitationDialog,
		isSummativeTest,
		postAnswerPanelValue,
		shouldRenderPostAnswer,
		shouldShowAiDisclaimer,
	} = usePostAnswerAppletValue({
		isSplitLayout,
		openAiDisclaimer: aiDisclaimerDialog.open,
		suggestCorrectionToggle: suggestCorrectionDialog.toggle,
	});

	const handleConfirmAiDisclaimer = useCallback(() => {
		aiDisclaimerDialog.close();
		setTutorialData({ hasSeenAiDisclaimer: true });
	}, [aiDisclaimerDialog.close, setTutorialData]);

	return (
		<>
			<AiSessionFab
				itemId={currentItem.id!}
				shouldRender={shouldRenderPostAnswer && ailaEnabled}
				shouldShowAiDisclaimer={shouldShowAiDisclaimer}
				handleShowAiDisclaimer={aiDisclaimerDialog.open}
			/>
			{shouldRenderPostAnswer && (
				<PostAnswerPanel value={postAnswerPanelValue}>
					{postAnswerPanelChildren}
				</PostAnswerPanel>
			)}
			{suggestCorrectionDialog.visible && (
				<FeedbackDialogContainer
					visible={suggestCorrectionDialog.visible}
					onDismiss={suggestCorrectionDialog.toggle}
					title={t("dialog.suggestCorrection.title")}
					subtitle={t("dialog.suggestCorrection.body")}
					icon="flag-outline"
					cta={t("dialog.suggestCorrection.cta")}
					itemId={`${currentItem.id!}`}
					sessionType={isSummativeTest ? "SUMMATIVE" : "FORMATIVE"}
					type="Content Flagged"
				/>
			)}
			{shouldShowAiDisclaimer && (
				<ConfirmDialog
					visible={aiDisclaimerDialog.visible}
					dismissCallback={aiDisclaimerDialog.close}
					title={aiDisclaimer?.title ?? ""}
					body={interpolateString(aiDisclaimer?.body ?? "", {
						agentName: aiAssistantTitle,
					})}
					confirmCallback={handleConfirmAiDisclaimer}
					confirmCta="Got it"
					icon="alert-outline"
				/>
			)}
			<CitationSourceDialog
				pageNumber={citationsPageNumber}
				visible={citationsPageNumber !== null}
				handleClose={handleCloseCitationDialog}
			/>
		</>
	);
};

export default StudySessionFeedbackContainer;
